import { Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LANGUAGES } from "@/types/employee-config";

/**
 * Language picker matching the instance employee editor: a Select that
 * adds a language (uppercase code + name, already-picked disabled) with
 * removable chips below.
 */
export function LanguagesField({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (languages: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("common");
  return (
    <div className="space-y-1.5">
      <Select
        value=""
        onValueChange={(code) => {
          if (code && !value.includes(code)) onChange([...value, code]);
        }}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder={t("fields.languages.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {LANGUAGES.map((l) => (
            <SelectItem
              key={l.value}
              value={l.value}
              disabled={value.includes(l.value)}
            >
              <span className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase">{l.value}</span>
                <span>{l.label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {value.map((code) => {
            const l = LANGUAGES.find((x) => x.value === code);
            return (
              <span
                key={code}
                className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
              >
                <span className="text-xs font-bold uppercase">{code}</span>
                <span>{l?.label ?? code}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onChange(value.filter((c) => c !== code))}
                    className="hover:opacity-70"
                    aria-label={t("fields.languages.remove", { code })}
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
